import { useState, useEffect, useMemo } from 'react';
import Swal from 'sweetalert2';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Stack,
    Typography,
    ButtonGroup,
    Skeleton
} from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import AddCardIcon from '@mui/icons-material/AddCard';

import { chipPaymentStatus } from '../../utils/chipPaymentStatus';
import { formatToLongDate } from '../../utils/formatToLongDate';
import { chipOrderStatus } from '../../utils/chipOrderStatus';
import { printListedOrders } from '../../utils/printListedOrders';
import SearchNotFound from '../../utils/SearchNotFound';

import { useLockBodyScroll } from '../utils';
import { formatCurrency } from '../utils';

import EditOrder from '../manage_orders/EditOrder';
import PaymentOrder from '../manage_payments/PaymentOrder';
import AddOrder from './AddOrder';
import MenuOrderStatus from './MenuOrderStatus';
import BarcodeOrder from './BarcodeOrder';

import CustomSearch from '../custom_components/CustomSearch';
import CustomButton from '../custom_components/CustomButton';
import CustomRefreshButton from '../custom_components/CustomRefreshButton';
import CustomIconButton from '../custom_components/CustomIconButton';
import CustomSelect from '../custom_components/CustomSelect';

const ArrivedListViewer = ({ listInfo, onBack }) => {

    const apiUrl = process.env.REACT_APP_API_URL;

    const [loading, setLoading] = useState(true);
    const [listOrders, setListOrders] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [tabChange, setTabChange] = useState('');

    const keyword = searchTerm.toLowerCase();
    const searchOrder = listOrders.filter(order =>
        order.CustomerName.toLowerCase().includes(keyword) ||
        order.ItemName.toLowerCase().includes(keyword)
    );

    const [openPaymentOrder, setOpenPaymentOrder] = useState(false);
    const [openEditOrder, setOpenEditOrder] = useState(false);
    const [openAddOrder, setOpenAddOrder] = useState(false);
    const [orderInfo, setOrderInfo] = useState({});

    useLockBodyScroll(openEditOrder);
    useLockBodyScroll(openPaymentOrder);
    useLockBodyScroll(openAddOrder);

    // DISPLAY LISTED ORDER
    const handleDisplayListOrder = async () => {
        setListOrders([]);
        setLoading(true);

        try {
            const response = await fetch(
                `${apiUrl}manage_arrived/get_arrived_order_list.php`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        listID: listInfo.ListID
                    })
                });

            const result = await response.json();

            if (result.status === 'success') {

                setListOrders(result.data || []);

            } else if (result.status === 'error') {
                Swal.fire({
                    title: 'Server Error!',
                    text: result.message,
                    icon: 'error'
                });
            }
        } catch (error) {
            Swal.fire({
                title: 'Server Error!',
                text: error,
                icon: 'error'
            });
            console.error('Error getting orders:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        handleDisplayListOrder();
    }, []);

    const groupedData = useMemo(() => {
        const filteredOrders = searchOrder.filter(order => {
            if (!tabChange) return true;

            const pay = order?.PayStatus;
            const status = order?.OrderStat;

            switch (tabChange) {
                case 'Paid':
                    return pay === 'Paid' && status === 'Arrived';
                case 'Pending':
                    return (pay === 'Partially' || pay === 'Pending') && status === 'Arrived'
                case 'Shipped':
                    return pay === 'Paid' && status === 'Shipped';
                case 'Forfeited':
                    return status === 'Forfeited';
                case 'Cancelled':
                    return status === 'Cancelled';
                default:
                    return true;
            }
        });

        return Array.from(
            new Map(filteredOrders.map(order => [order.OrderID, order])).values()
        );

    }, [searchOrder, tabChange]);

    const handleAddEditOrder = (info, type) => {
        setOrderInfo(info);

        const actions = {
            edit: () => setOpenEditOrder(true),
            add_payment: () => setOpenPaymentOrder(true),
        };

        actions[type]?.();
    };

    const handlePrintList = () => {
        const list_title =
            `${listInfo.ListName} ${formatToLongDate(listInfo.DateArrived).toUpperCase()}`;
        printListedOrders(listOrders, list_title);
    };

    return (
        <Box>

            <Stack flexDirection="row" gap={2} alignItems="center">
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        color: '#1a1a1a',
                        letterSpacing: 0.2,
                    }}
                >
                    {listInfo.ListName}
                </Typography>
                <Typography
                    variant="body1"
                    sx={{
                        color: '#757575',
                        fontWeight: 500,
                    }}
                >
                    {formatToLongDate(listInfo.DateArrived)}
                </Typography>
            </Stack>

            {/* SEARCH, BUTTONS */}
            <Stack flexDirection="row" justifyContent="space-between" mt={2}>

                <Stack flexDirection="row" gap={2}>
                    <CustomSearch
                        md='46ch'
                        placeholder='Search by Customer Name, Item Name'
                        value={searchTerm}
                        onChange={(val) => setSearchTerm(val)}
                    />

                    <CustomRefreshButton
                        label="Refresh"
                        loading={loading}
                        onClick={handleDisplayListOrder}
                    />

                    <CustomSelect
                        label="Filter by Status"
                        value={tabChange}
                        minWidth={160}
                        onChange={setTabChange}
                        options={[
                            { label: <em>All</em>, value: "" },
                            { label: "Pending", value: "Pending" },
                            { label: "Paid", value: "Paid" },
                            { label: "Shipped", value: "Shipped" },
                            { label: "Forfeited", value: "Forfeited" },
                            { label: "Cancelled", value: "Cancelled" },
                        ]}
                    />
                </Stack>

                <Stack direction="row" gap={2}>
                    <ButtonGroup direction='row' gap={2}>
                        <CustomButton
                            label="Add Order"
                            startIcon={<AddShoppingCartIcon />}
                            onClick={() => setOpenAddOrder(true)}
                        />
                        <CustomButton
                            label="Print"
                            startIcon={<PrintIcon />}
                            onClick={handlePrintList}
                        />
                    </ButtonGroup>

                    <CustomButton
                        label="Back"
                        variant="borderless"
                        hoverColor="#1976d2"
                        startIcon={<ArrowBackIosIcon />}
                        onClick={onBack}
                    />
                </Stack>

            </Stack>

            {/* DISPLAY LIST UI */}
            <TableContainer
                sx={{
                    mb: 1,
                    mt: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1.2,
                    overflow: 'hidden',
                    backgroundColor: '#fff',
                }}
            >
                <Table size="small">

                    {/* HEADER */}
                    <TableHead>
                        <TableRow
                            sx={{
                                backgroundColor: '#111',
                                height: 42,
                            }}
                        >
                            {[
                                'Customer Name',
                                'Item Name',
                                'Qty',
                                'Paid Amount',
                                'Balance',
                                'Payment Status',
                                'Order Status',
                                'Actions'
                            ].map((header, index) => (
                                <TableCell key={header} align={header === "Actions" ? "center" : "left"} sx={{
                                    bgcolor: "#1C2536",
                                    color: "#94A3B8",
                                    fontWeight: 700,
                                    fontSize: 11,
                                    letterSpacing: "0.6px",
                                    textTransform: "uppercase",
                                    borderBottom: "none",
                                    py: 1,
                                    width: header === "#" ? 48 : header === "Actions" ? 110 : "auto",
                                }}>
                                    {header}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    {/* BODY */}
                    <TableBody>

                        {/* LOADING */}
                        {loading ? (

                            [...Array(6)].map((_, index) => (
                                <TableRow
                                    key={index}
                                    sx={{
                                        "& td": {
                                            borderBottom: "1px solid #f1f1f1",
                                            py: 1,
                                        },
                                    }}
                                >
                                    {/* CUSTOMER */}
                                    <TableCell>
                                        <Skeleton width={140} height={22} />
                                    </TableCell>

                                    {/* ITEM */}
                                    <TableCell>
                                        <Skeleton width={180} height={22} />
                                    </TableCell>

                                    {/* QTY */}
                                    <TableCell>
                                        <Skeleton width={40} height={22} />
                                    </TableCell>

                                    {/* PAID AMOUNT */}
                                    <TableCell>
                                        <Skeleton width={80} height={22} />
                                    </TableCell>

                                    {/* BALANCE */}
                                    <TableCell>
                                        <Skeleton width={80} height={22} />
                                    </TableCell>

                                    {/* PAYMENT STATUS */}
                                    <TableCell>
                                        <Skeleton
                                            variant="rounded"
                                            width={90}
                                            height={24}
                                            sx={{ borderRadius: 1 }}
                                        />
                                    </TableCell>

                                    {/* ORDER STATUS */}
                                    <TableCell>
                                        <Skeleton
                                            variant="rounded"
                                            width={90}
                                            height={24}
                                            sx={{ borderRadius: 1 }}
                                        />
                                    </TableCell>

                                    {/* ACTIONS */}
                                    <TableCell align="center">
                                        <Stack
                                            direction="row"
                                            justifyContent="center"
                                            spacing={1}
                                        >
                                            <Skeleton variant="circular" width={30} height={30} />
                                            <Skeleton variant="circular" width={30} height={30} />
                                            <Skeleton variant="circular" width={30} height={30} />
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))

                        ) : groupedData.length > 0 ? (

                            groupedData.map((order) => {
                                const balance =
                                    ((Number(order.Price || 0) * Number(order.ItemQty || 0))
                                        + Number(order.SFee || 0))
                                    - Number(order.PaidAmount || 0);

                                return (
                                    <TableRow
                                        key={order.OrderID}
                                        sx={{
                                            cursor: "pointer",
                                            transition: "all .2s ease",
                                            "&:hover": {
                                                backgroundColor: "#dcfbff",
                                            },

                                            '& td': {
                                                borderBottom: '1px solid #f1f1f1',
                                                py: 1,
                                                fontSize: 13,
                                            },
                                        }}
                                    >

                                        {/* CUSTOMER */}
                                        <TableCell>
                                            <Stack spacing={0}>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 600,
                                                        color: '#222',
                                                    }}
                                                >
                                                    {order.CustomerName}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" >
                                                    {order.SecondName}
                                                </Typography>
                                            </Stack>
                                        </TableCell>

                                        {/* ITEM */}
                                        <TableCell
                                            sx={{
                                                fontWeight: 600,
                                                color: '#222',
                                            }}
                                        >
                                            {order.ItemName}
                                        </TableCell>

                                        {/* QTY */}
                                        <TableCell
                                            sx={{
                                                fontWeight: 600,
                                                color: '#222',
                                            }}
                                        >
                                            {order.ItemQty}x
                                        </TableCell>

                                        {/* PAID AMOUNT */}
                                        <TableCell>
                                            <Typography
                                                sx={{
                                                    fontSize: 13,
                                                    fontWeight: 600,
                                                    color: Number(order.PaidAmount) > 0
                                                        ? '#059669'
                                                        : '#94A3B8',
                                                }}
                                            >
                                                {Number(order.PaidAmount) > 0
                                                    ? `₱${formatCurrency(order.PaidAmount)}`
                                                    : 'No DP'}
                                            </Typography>
                                        </TableCell>

                                        {/* BALANCE */}
                                        <TableCell >
                                            <Typography
                                                sx={{
                                                    fontSize: balance > 0 ? 13 : 12,
                                                    fontWeight: 600,
                                                    color: balance > 0 ? "#d32f2f" : "#64748B",
                                                }}
                                            >
                                                {balance > 0
                                                    ? `₱${formatCurrency(balance)}`
                                                    : <em>Fully Paid</em>}
                                            </Typography>
                                        </TableCell>

                                        {/* PAYMENT */}
                                        <TableCell>
                                            {chipPaymentStatus(order.PayStatus)}
                                        </TableCell>

                                        {/* STATUS */}
                                        <TableCell>
                                            {chipOrderStatus(order.OrderStat)}
                                        </TableCell>

                                        {/* ACTION */}
                                        <TableCell align="center">
                                            <Stack
                                                direction="row"
                                                justifyContent="center"
                                                alignItems="center"
                                                spacing={1.2}
                                            >
                                                {/* EDIT ORDER INFO */}
                                                <CustomIconButton
                                                    type="success"
                                                    icon={<EditIcon fontSize="small" />}
                                                    onClick={() => handleAddEditOrder(order, "edit")}
                                                />

                                                {/* ADD PAYMENT FOR ORDER */}
                                                <CustomIconButton
                                                    type="info"
                                                    icon={<AddCardIcon fontSize="small" />}
                                                    onClick={() => handleAddEditOrder(order, "add_payment")}
                                                />

                                                <MenuOrderStatus
                                                    orderInfo={order}
                                                    onUpdate={(val) => setListOrders(prev =>
                                                        prev.map(item =>
                                                            item.OrderID === val.orderID &&
                                                                item.CustomerID === val.customerID
                                                                ? {
                                                                    ...item,
                                                                    OrderStat: val.status,
                                                                }
                                                                : item
                                                        )
                                                    )}
                                                />

                                            </Stack>
                                        </TableCell>

                                    </TableRow>
                                )
                            })

                        ) : (

                            /* EMPTY */
                            <TableRow>
                                <TableCell
                                    colSpan={7}
                                    sx={{
                                        borderBottom: 'none',
                                    }}
                                >

                                    <SearchNotFound />
                                </TableCell>
                            </TableRow>

                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* EDIT ORDER */}
            <EditOrder
                open={openEditOrder}
                onClose={() => setOpenEditOrder(false)}

                orderInfo={orderInfo}
                onUpdate={(val) =>
                    setListOrders(prev =>
                        prev.map(item =>
                            item.OrderID === val.orderID &&
                                item.CustomerID === val.customerID
                                ? {
                                    ...item,
                                    ItemName: val.ItemName,
                                    ItemQty: val.ItemQty,
                                    ItemCategory: val.ItemCategory,
                                    Price: val.Price,
                                    SFee: val.SFee,
                                    Cargo: val.Cargo,
                                }
                                : item
                        )
                    )
                }
            />

            {/* ADD ORDER PAYMENT */}
            <PaymentOrder
                open={openPaymentOrder}
                onClose={() => setOpenPaymentOrder(false)}

                orderInfo={orderInfo}
                onUpdate={(val) => setListOrders(prev =>
                    prev.map(item =>
                        item.OrderID === val.OrderID &&
                            item.CustomerID === val.CustomerID
                            ? {
                                ...item,
                                PaidAmount: val.PaidAmount,
                            }
                            : item
                    )
                )}
                onUpdateStatus={(val) => setListOrders(prev =>
                    prev.map(item =>
                        item.OrderID === val.OrderID &&
                            item.CustomerID === val.CustomerID
                            ? {
                                ...item,
                                PayStatus: val.payStatus,
                            }
                            : item
                    )
                )}
            />

            {/* ADD ORDER TO LIST */}
            <AddOrder
                open={openAddOrder}
                onClose={() => setOpenAddOrder(false)}
                listID={listInfo.ListID || ""}
                listOrders={listOrders || []}

                onUpdate={(val) =>
                    setListOrders(prev => [
                        val,
                        ...prev,
                    ])
                }
            />

            {/* ADD ORDER USING BARCODE SCAN */}
            <BarcodeOrder
                listID={listInfo.ListID || ""}
                onUpdate={(val) =>
                    setListOrders(prev => [
                        val,
                        ...prev,
                    ])
                }
            />

        </Box>
    );

};

export default ArrivedListViewer;
